import React, { ChangeEvent, useCallback, useState } from 'react';
import MultiTrackChangeIndicator from '@/components/common/MultiTrackChangeIndicator/MultiTrackChangeIndicator';
import { isEqual } from 'lodash';
import { IMixedField } from '@/data/models/trackMixed';
import { useMixedPlaceholder } from '@/hooks/useMixedPlaceholder';
import { VALIDATION_STATE_WARNING } from '@/utils/validationErrors';
import ReadOnlyField from './ReadOnlyField/ReadOnlyField';

interface CopyrightInputProps {
  id: string;
  labelText: string;
  linePlaceholderLabelText: string;
  yearPlaceholderLabelText?: string | null;
  year?: number | null;
  originalYear?: number | null;
  line?: string | null;
  originalLine?: IMixedField | null;
  onYearChange?: (year: number | string | null) => void;
  onLineChange?: (line: string | null) => void;
  lineErrorMessage?: string | null;
  yearErrorMessage?: string | null;
  lineValidationState?: string | null;
  yearValidationState?: string | null;
  readonly?: boolean;
  disabled?: boolean;
  labelClassName?: string;
  inputPairClassName?: string;
  errorMessageClassName?: string;
  displayLineBuilder: (
    year: number | null | undefined,
    line: string | null | undefined
  ) => string;
  multiTrack?: boolean;
  showChangeIndicator?: boolean;
}

const DEFAULT_ERROR_MESSAGE_CLASS_NAME = 'col-sm-2 popUp-container';
const WARNING_ERROR_MESSAGE_CLASS_NAME = 'col-sm-2 popUp-container-warning';

const getValidationClassName = (validationState?: string | null) =>
  validationState === VALIDATION_STATE_WARNING
    ? 'warning-input'
    : 'invalid-input';

const CopyrightInput = ({
  id,
  labelText,
  linePlaceholderLabelText,
  yearPlaceholderLabelText,
  year,
  originalYear,
  line,
  originalLine,
  onYearChange,
  onLineChange,
  lineErrorMessage,
  yearErrorMessage,
  lineValidationState,
  yearValidationState,
  readonly,
  disabled,
  labelClassName,
  inputPairClassName,
  errorMessageClassName,
  displayLineBuilder,
  multiTrack = false,
  showChangeIndicator = false,
}: CopyrightInputProps) => {
  const [lineChanged, setLineChanged] = useState(false);
  const [yearChanged, setYearChanged] = useState(false);
  const [isLineFocused, setIsLineFocused] = useState(false);
  const [isYearFocused, setIsYearFocused] = useState(false);

  const displayCopyrightLine = displayLineBuilder(year, line);
  const yearSymbol = id === 'copyrightLine' ? '©' : '℗';
  const readOnlyYear = year ? `${yearSymbol} ${year}` : null;

  const handleYearInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const year = parseInt(evt.target.value);
    onYearChange?.(isNaN(year) ? null : year);
    setYearChanged(true);
  };

  const handleLineInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const line = evt.target.value || null;
    onLineChange?.(line);
    setLineChanged(true);
  };

  const undoChanges = useCallback(() => {
    if (originalYear === ('Mixed' as any)) {
      onYearChange?.('Mixed' as any);
    } else {
      const parsedYear =
        typeof originalYear === 'number'
          ? originalYear
          : parseInt(String(originalYear), 10);
      onYearChange?.(Number.isNaN(parsedYear) ? null : parsedYear);
    }
    onLineChange?.((originalLine?.originalValue as string) || null);
  }, [originalYear, originalLine?.originalValue, onYearChange, onLineChange]);

  const isYearMixedPlaceholder = multiTrack && (year as unknown) == 'Mixed';
  const isLineMixedPlaceholder =
    multiTrack &&
    line === 'Mixed' &&
    originalLine?.originalValue === 'Mixed' &&
    originalLine.isMixed;

  const getYearClassName = () => {
    const validationClassName = getValidationClassName(yearValidationState);
    const className =
      'form-control ' +
      (yearErrorMessage && !yearChanged ? validationClassName : '');
    if (isYearMixedPlaceholder) {
      return className + ' mixed-input-text';
    }
    return className;
  };

  const getLineClassName = () => {
    const validationClassName = getValidationClassName(lineValidationState);
    const className =
      'form-control ' +
      (lineErrorMessage && !lineChanged ? validationClassName : '');
    if (isLineMixedPlaceholder) {
      return className + ' mixed-input-text';
    }
    return className;
  };

  const getMessageClassName = (validationState?: string | null) => {
    if (validationState === VALIDATION_STATE_WARNING) {
      return WARNING_ERROR_MESSAGE_CLASS_NAME;
    }

    return errorMessageClassName ?? DEFAULT_ERROR_MESSAGE_CLASS_NAME;
  };

  const yearMixedPlaceholder = useMixedPlaceholder(isYearMixedPlaceholder, {
    onFocus: () => {
      setIsYearFocused(true);
    },
    onBlur: () => {
      setIsYearFocused(false);
    },
  });

  const inputRoleMixedPlaceholder = useMixedPlaceholder(
    isLineMixedPlaceholder,
    {
      onFocus: () => {
        setIsLineFocused(true);
      },
      onBlur: () => {
        setIsLineFocused(false);
      },
    }
  );

  return (
    <>
      {!readonly ? (
        <div className="copyright-input-container">
          <label className={labelClassName}>{labelText}</label>
          {multiTrack && showChangeIndicator ? (
            <MultiTrackChangeIndicator
              show={
                !disabled &&
                (!isEqual(year, originalYear) ||
                  !isEqual(line, originalLine?.originalValue))
              }
              onClick={() => undoChanges()}
            ></MultiTrackChangeIndicator>
          ) : null}
          <div className={inputPairClassName}>
            <div className="col-sm-12 padding-right-0">
              <div className="pair-input">
                <div className="fixed-left">
                  <input
                    placeholder={yearPlaceholderLabelText ?? 'YYYY'}
                    className={getYearClassName()}
                    type="text"
                    maxLength={4}
                    value={year ?? ''}
                    onChange={handleYearInputChange}
                    disabled={disabled}
                    {...yearMixedPlaceholder}
                  />
                </div>
                <div className="right-fill">
                  <input
                    placeholder={linePlaceholderLabelText}
                    className={getLineClassName()}
                    type="text"
                    value={line ?? ''}
                    onChange={handleLineInputChange}
                    maxLength={247}
                    disabled={disabled}
                    {...inputRoleMixedPlaceholder}
                  />
                </div>
              </div>
            </div>
            <div className="col-sm-12 help-block-container">
              <div className="form-text">{displayCopyrightLine}</div>
            </div>
          </div>
          {lineErrorMessage && !lineChanged && isLineFocused ? (
            <span className={getMessageClassName(lineValidationState)}>
              {lineErrorMessage}
            </span>
          ) : null}
          {yearErrorMessage && !yearChanged && isYearFocused ? (
            <span className={getMessageClassName(yearValidationState)}>
              {yearErrorMessage}
            </span>
          ) : null}
        </div>
      ) : (
        <ReadOnlyField
          id={id}
          labelTitle={labelText}
          value={[readOnlyYear, line]}
        ></ReadOnlyField>
      )}
    </>
  );
};

export default CopyrightInput;
